<template>
  <div class="page-container">
    <el-card>
      <!-- 搜索区 -->
      <div class="search-bar">
        <el-input
          v-model="query.keywords"
          placeholder="服务订单号 / 用户姓名 / 手机号"
          clearable
          style="width:240px"
          @keyup.enter.native="handleSearch"
        />
        <el-select v-model="query.status" placeholder="服务订单状态" clearable style="width:140px">
          <el-option label="待录入" :value="0" />
          <el-option label="待服务" :value="1" />
          <el-option label="待分配" :value="2" />
          <el-option label="待联系" :value="3" />
          <el-option label="待上门" :value="4" />
          <el-option label="待发件" :value="5" />
          <el-option label="待检测" :value="6" />
          <el-option label="已完成" :value="7" />
          <el-option label="已退款" :value="8" />
        </el-select>
        <el-button type="primary" size="mini" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button size="mini" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 表格 -->
      <el-table :data="tableData" v-loading="loading" border stripe style="width:100%">
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column prop="service_no" label="服务订单号" min-width="190" show-overflow-tooltip />
        <el-table-column label="服务订单状态" width="110" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="statusType(row.status)" size="small">
              {{ row.status_label || statusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="product_order_no" label="商品订单号" min-width="190" show-overflow-tooltip />
        <el-table-column label="用户姓名" width="100">
          <template slot-scope="{ row }">{{ row.user_name || '—' }}</template>
        </el-table-column>
        <el-table-column label="用户手机号" width="130">
          <template slot-scope="{ row }">{{ row.user_phone || '—' }}</template>
        </el-table-column>
        <el-table-column label="预约上门地址" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.appointment_address || '—' }}</template>
        </el-table-column>
        <el-table-column label="预约上门时间" width="160">
          <template slot-scope="{ row }">{{ row.appointment_at || '—' }}</template>
        </el-table-column>
        <el-table-column prop="product_name" label="商品名称" min-width="150" show-overflow-tooltip />
        <el-table-column label="操作" width="200" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-permission="['order:service:detail']"
              type="primary" size="mini" icon="el-icon-view"
              @click="$router.push(`/order/service/detail?id=${row.id}`)"
            >详情</el-button>
            <el-button
              v-if="row.status === 2"
              v-permission="['order:service:assignCollector']"
              type="warning" size="mini" icon="el-icon-user"
              @click="openAssignDialog(row)"
            >分配</el-button>
            <el-button
              v-if="row.status === 6"
              v-permission="['order:service:uploadReport']"
              type="success" size="mini" icon="el-icon-upload2"
              @click="openReportDialog(row)"
            >上传报告</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        :current-page="query.page"
        :page-sizes="[10, 15, 20, 50]"
        :page-size="query.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top:16px;text-align:right"
        @size-change="val => { query.pageSize = val; fetchData() }"
        @current-change="val => { query.page = val; fetchData() }"
      />
    </el-card>

    <!-- 采血师分配弹框 -->
    <el-dialog title="分配采血师" :visible.sync="assignDialog.visible" width="460px" @close="resetAssignForm">
      <el-form ref="assignForm" :model="assignForm" :rules="assignRules" label-width="120px" size="small">
        <el-form-item label="采血师姓名" prop="collector_name">
          <el-input v-model="assignForm.collector_name" placeholder="请输入采血师姓名" />
        </el-form-item>
        <el-form-item label="采血师手机号" prop="collector_phone">
          <el-input v-model="assignForm.collector_phone" placeholder="请输入采血师手机号" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="assignDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="assignDialog.submitting" @click="submitAssign">确定</el-button>
      </span>
    </el-dialog>

    <!-- 上传检测报告弹框 -->
    <el-dialog title="上传检测报告" :visible.sync="reportDialog.visible" width="440px" @close="resetReportDialog">
      <div class="upload-hint">请选择 PDF 格式的检测报告文件</div>
      <el-upload
        ref="reportUpload"
        action="#"
        accept=".pdf"
        :limit="1"
        :auto-upload="false"
        :on-change="onReportFileChange"
        :on-remove="() => { reportDialog.file = null }"
        :file-list="[]"
      >
        <el-button size="small" icon="el-icon-folder-opened">选择文件</el-button>
        <span slot="tip" class="el-upload__tip" style="margin-left:8px">仅支持 .pdf</span>
      </el-upload>
      <span slot="footer">
        <el-button @click="reportDialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="reportDialog.submitting" @click="submitReport">确定上传</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getServiceOrderList, assignCollector, uploadReport } from '@/api/order'

const SERVICE_STATUS = {
  0: { label: '待录入', type: 'info' },
  1: { label: '待服务', type: 'warning' },
  2: { label: '待分配', type: 'warning' },
  3: { label: '待联系', type: '' },
  4: { label: '待上门', type: '' },
  5: { label: '待发件', type: '' },
  6: { label: '待检测', type: '' },
  7: { label: '已完成', type: 'success' },
  8: { label: '已退款', type: 'danger' }
}

export default {
  name: 'ServiceOrderList',
  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      query: { page: 1, pageSize: 15, keywords: '', status: '' },
      assignDialog: {
        visible: false,
        submitting: false,
        serviceOrderId: null
      },
      assignForm: { collector_name: '', collector_phone: '' },
      assignRules: {
        collector_name:  [{ required: true, message: '请输入采血师姓名', trigger: 'blur' }],
        collector_phone: [{ required: true, message: '请输入采血师联系方式', trigger: 'blur' }]
      },
      reportDialog: {
        visible: false,
        submitting: false,
        serviceOrderId: null,
        file: null
      }
    }
  },
  created() { this.fetchData() },
  activated() { this.fetchData() },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const params = { ...this.query }
        if (params.status === '') delete params.status
        if (!params.keywords) delete params.keywords
        const res = await getServiceOrderList(params)
        const d = res.data || {}
        this.tableData = d.list || []
        this.total = d.total || 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() { this.query.page = 1; this.fetchData() },
    handleReset() {
      this.query = { page: 1, pageSize: 15, keywords: '', status: '' }
      this.fetchData()
    },

    // ── 采血师分配 ──
    openAssignDialog(row) {
      this.assignDialog.serviceOrderId = row.id
      this.assignDialog.visible = true
    },
    resetAssignForm() {
      this.assignForm = { collector_name: '', collector_phone: '' }
      if (this.$refs.assignForm) this.$refs.assignForm.clearValidate()
    },
    async submitAssign() {
      const valid = await this.$refs.assignForm.validate().catch(() => false)
      if (!valid) return
      this.assignDialog.submitting = true
      try {
        await assignCollector({
          service_order_id: this.assignDialog.serviceOrderId,
          collector_name: this.assignForm.collector_name,
          collector_phone: this.assignForm.collector_phone
        })
        this.$message.success('采血师分配成功')
        this.assignDialog.visible = false
        this.fetchData()
      } finally {
        this.assignDialog.submitting = false
      }
    },

    // ── 上传检测报告 ──
    openReportDialog(row) {
      this.reportDialog.serviceOrderId = row.id
      this.reportDialog.file = null
      this.reportDialog.visible = true
    },
    onReportFileChange(file) {
      this.reportDialog.file = file.raw
    },
    resetReportDialog() {
      this.reportDialog.file = null
      this.reportDialog.serviceOrderId = null
      if (this.$refs.reportUpload) this.$refs.reportUpload.clearFiles()
    },
    async submitReport() {
      if (!this.reportDialog.file) {
        return this.$message.warning('请先选择 PDF 文件')
      }
      this.reportDialog.submitting = true
      try {
        await uploadReport(this.reportDialog.serviceOrderId, this.reportDialog.file)
        this.$message.success('检测报告上传成功')
        this.reportDialog.visible = false
        this.fetchData()
      } catch (e) {
        this.$message.error(e.message || '上传失败，请重试')
      } finally {
        this.reportDialog.submitting = false
      }
    },

    statusType(s) { return (SERVICE_STATUS[s] || {}).type || '' },
    statusLabel(s) { return (SERVICE_STATUS[s] || {}).label || String(s) }
  }
}
</script>

<style lang="scss" scoped>
.search-bar  { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
.upload-hint { color: #606266; font-size: 13px; margin-bottom: 12px; }
</style>
